import * as _ from 'lodash-es';
import Quill from 'quill/core';
import type { Range } from 'quill/core';
import { useEffect, useState } from 'react';
import transformRange from './transformRange';

export function getRangeSaver(quill: Quill): RangeSaver {
  return quill.getModule('rangeSaver') as RangeSaver;
}

const Module = Quill.import('core/module');

const EVENT_NAME = 'range-saver-change';

class RangeSaver extends Module {
  private range: Range = { index: 0, length: 0 };

  constructor(quill: Quill, options: Record<string, unknown>) {
    super(quill, options);

    this.quill.on(Quill.events.EDITOR_CHANGE, (...args) => {
      if (args[0] === Quill.events.SELECTION_CHANGE) {
        const range = args[1];
        if (range) {
          this.setLast({ index: range.index, length: range.length });
        }
      } else if (args[0] === Quill.events.TEXT_CHANGE) {
        const [, change, , source] = args;
        if (source === Quill.sources.API) {
          this.setLast(transformRange(this.getLast(), change));
        }
      }
    });
  }

  private setLast(range: Range) {
    this.range = range;
    this.quill.emitter.emit(EVENT_NAME);
  }

  getLast() {
    return this.range;
  }
}

export default RangeSaver;

export const useRangeSaver = (quill: Quill | null) => {
  const [range, setRange] = useState<Range | null>(
    quill ? getRangeSaver(quill).getLast() : null,
  );

  useEffect(() => {
    if (!quill) return undefined;

    setRange((prevRange) => {
      const newRange = getRangeSaver(quill).getLast();
      return _.isEqual(prevRange, newRange) ? prevRange : newRange;
    });

    const listener = () => {
      setRange(getRangeSaver(quill).getLast());
    };

    quill.on(EVENT_NAME, listener);
    return () => {
      quill.off(EVENT_NAME, listener);
    };
  }, [quill]);

  return range;
};
